import { useEffect, useRef, useState } from 'react';

import { datadogLogs } from 'root/datadog/datadogLogs';

import { MFE_SCRIPT_URL } from './config';

interface UseMASScriptResult {
  isLoaded: boolean;
  hasError: boolean;
}

/**
 * Hook to load the MAS MFE script and wait for a custom element to be defined.
 * Handles script deduplication if already loaded by another component.
 *
 * @param customElementName - The name of the custom element to wait for (e.g., 'merchant-account-notification')
 * @param onReady - Callback fired when the custom element is defined and ready
 */
export const useMASScript = (
  customElementName: string,
  onReady: () => void
): UseMASScriptResult => {
  const scriptRef = useRef<HTMLScriptElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const handleReady = () => {
      setIsLoaded(true);
      onReady();
    };

    // Check if custom element is already defined
    if (customElements.get(customElementName)) {
      handleReady();
      return undefined;
    }

    // Check if script already exists in the DOM (loaded by another component)
    const existingScript = document.querySelector(`script[src="${MFE_SCRIPT_URL}"]`);
    if (existingScript) {
      customElements.whenDefined(customElementName).then(handleReady);
      return undefined;
    }

    // Load the script
    const script = document.createElement('script');
    script.src = MFE_SCRIPT_URL;
    script.type = 'module';
    script.onload = () => {
      customElements.whenDefined(customElementName).then(handleReady);
    };
    script.onerror = () => {
      datadogLogs.logger.error('LSR-36587 MASScript: Failed to load', {
        customElementName,
        scriptUrl: MFE_SCRIPT_URL,
      });
      setHasError(true);
    };
    document.head.appendChild(script);
    scriptRef.current = script;

    return () => {
      // Only remove script if we added it and no MAS components are using it
      const hasMASComponents =
        document.querySelector('merchant-account-space') ||
        document.querySelector('merchant-account-notification');
      if (scriptRef.current && !hasMASComponents) {
        document.head.removeChild(scriptRef.current);
        scriptRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customElementName]);

  return { isLoaded, hasError };
};
